import { createContext, useContext, useEffect, useMemo, useState } from "react"

import Color from "color"

import { useMe } from "./useMe"

export const deviceSizes = {
  mobileS: 320,
  mobileM: 375,
  mobileL: 425,
  tablet: 768,
  laptop: 1024,
  laptopL: 1440,
  desktop: 2560,
}

const sat = 60
const lum = 80

export const colorMap = {
  red: `hsl(0deg ${sat}% ${lum}%)`,
  orange: `hsl(29deg ${sat}% ${lum}%)`,
  yellow: `hsl(43deg ${sat}% ${lum}%)`,
  green: `hsl(96deg ${sat}% ${lum}%)`,
  blue: `hsl(217deg ${sat}% ${lum}%)`,
  grey: "#e0e1e2",
  msteams: "#6264a7",
}

// https://github.com/Qix-/color/blob/e188999dee229c902102ec37e398ff4d868616e5/index.js#L298
export const isDark = (color, threshold = 145) => {
  const rgb = color.rgb().color
  const yiq = (rgb[0] * 2126 + rgb[1] * 7152 + rgb[2] * 722) / 10000
  return yiq < threshold
}

type UseThemeProps = {
  organisation?: {
    primaryColour?: string
    secondaryColour?: string
  }
}

type Theme = {
  sizes: {
    isMobile: boolean
    isTablet: boolean
    isComputer: boolean
    isLargeComputer: boolean
    width: number
    minStyle?: Record<string, string>
    maxStyle?: Record<string, string>
  }
  primaryColour: string
  secondaryColour: string
  accent: string
  secondaryAccent: string
  shouldBeWhiteOnSecondary: boolean
  shouldBeWhiteOnPrimary?: boolean
  shouldBeWhiteOnAccent: boolean
  shouldBeWhiteOnSecondaryAccent: boolean
  boxShadow?: string
  colorMap?: Record<string, string>
}

const SizeContext = createContext<Theme["sizes"] | null>(null)

export const ThemeProvider = ({ children }) => {
  const { innerWidth } = window
  const [width, setInnerWidth] = useState(innerWidth)
  const [sizes, setSizes] = useState<Theme["sizes"]>({
    width,
    isMobile: width < deviceSizes.tablet,
    isTablet: width >= deviceSizes.tablet,
    isComputer: width >= deviceSizes.laptop,
    isLargeComputer: width >= deviceSizes.laptopL,
  })

  useEffect(() => {
    const updateDimensions = () => {
      const { innerWidth: currentInnerWidth } = window
      setInnerWidth(currentInnerWidth)
    }

    window.addEventListener("resize", updateDimensions)

    return () => {
      window.removeEventListener("resize", updateDimensions)
    }
  }, [])

  useEffect(() => {
    const isMobile = width < deviceSizes.tablet
    const isTablet = width >= deviceSizes.tablet
    const isComputer = width >= deviceSizes.laptop
    const isLargeComputer = width >= deviceSizes.laptopL
    const sizes = {
      isMobile,
      isTablet,
      isComputer,
      isLargeComputer,
      width,
      minStyle: Object.entries(deviceSizes).reduce(
        (curr, [key, val]) => ({ ...curr, [key]: `min-width: ${val}px;` }),
        {},
      ),
      maxStyle: Object.entries(deviceSizes).reduce(
        (curr, [key, val]) => ({ ...curr, [key]: `max-width: ${val}px;` }),
        {},
      ),
    }

    setSizes(sizes)
  }, [width])

  return <SizeContext.Provider value={sizes}>{children}</SizeContext.Provider>
}

export const useTheme = (props?: UseThemeProps) => {
  const sizes = useContext(SizeContext)
  if (!sizes) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  const me = useMe()
  const { primaryColour = "#14435B", secondaryColour = "#1b678e" } = props?.organisation || me?.ownerOrganisation || {}

  const accent = Color(primaryColour).lighten(0.2)
  const secondaryAccent = Color(secondaryColour).lighten(0.6).fade(0.8)

  const fullTheme = useMemo(
    () => ({
      sizes,
      primaryColour,
      secondaryColour,
      accent: accent.hex(),
      secondaryAccent: secondaryColour ? secondaryAccent.hex() : accent.hex(),
      shouldBeWhiteOnSecondary: isDark(Color(secondaryColour)),
      shouldBeWhiteOnPrimary: isDark(Color(primaryColour)),
      shouldBeWhiteOnAccent: isDark(accent),
      shouldBeWhiteOnSecondaryAccent: isDark(secondaryAccent),
      boxShadow: "0.5px 0.5px 2.5px 1px #C0C0C0",
      colorMap,
    }),
    [sizes, primaryColour, secondaryColour, accent, secondaryAccent],
  )

  return fullTheme
}
